import type { FieldDef, FieldValue, Kind } from "./model";

export class FieldError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "FieldError";
  }
}

/**
 * Checks a patch of field values against a kind's field definitions and returns the cleaned patch.
 * Unknown keys are rejected so that agents learn the schema instead of writing junk.
 */
export function cleanFields(kind: Pick<Kind, "name" | "fields">, patch: Record<string, FieldValue>): Record<string, FieldValue> {
  const defs = new Map(kind.fields.map((f) => [f.key, f]));
  const out: Record<string, FieldValue> = {};
  for (const [key, value] of Object.entries(patch)) {
    const def = defs.get(key);
    if (!def) {
      const known = kind.fields.map((f) => f.key).join(", ") || "none";
      throw new FieldError(`${kind.name} has no field "${key}". Fields: ${known}.`);
    }
    out[key] = value === null ? null : coerce(def, value);
  }
  return out;
}

function coerce(def: FieldDef, value: FieldValue): FieldValue {
  const bad = (want: string) => new FieldError(`${def.label} must be ${want}.`);
  switch (def.type) {
    case "text":
    case "url":
    case "actor":
      if (typeof value !== "string") throw bad("text");
      if (def.type === "url" && value !== "" && !/^https?:\/\//.test(value)) throw bad("a link starting with http");
      return value;
    case "number": {
      const n = typeof value === "string" && value.trim() !== "" ? Number(value) : value;
      if (typeof n !== "number" || !Number.isFinite(n)) throw bad("a number");
      return n;
    }
    case "checkbox":
      if (typeof value !== "boolean") throw bad("true or false");
      return value;
    case "date":
      if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(value)) throw bad("a date like 2026-10-01");
      return value;
    case "select":
      if (typeof value !== "string") throw bad("one option");
      if (def.options && !def.options.includes(value)) throw bad(`one of: ${def.options.join(", ")}`);
      return value;
    case "multiselect": {
      if (!Array.isArray(value)) throw bad("a list of options");
      const bad2 = value.find((v) => def.options && !def.options.includes(v));
      if (bad2 !== undefined) throw bad(`chosen from: ${(def.options ?? []).join(", ")}`);
      return value;
    }
  }
}

export function formatFieldValue(def: FieldDef, value: FieldValue | undefined): string {
  if (value === null || value === undefined || value === "") return "";
  if (Array.isArray(value)) return value.join(", ");
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return def.unit ? `${value} ${def.unit}` : String(value);
  return value;
}
